
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { getFeatures, getLimits } from 'admin/services/features/actions'
import { impersonationSelector } from 'admin/services/selectors'
import { Observable, firstValueFrom, throwError as observableThrowError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { message } from 'services/snackbar'
import { HS_EVENT_SUBSCRIPTION_CHANGE, trackHubspotEvent } from 'services/tracking/hubspot'
import { userProductSelector } from 'services/users/selectors'
import { LIMIT_SCHEDULED_POSTS_COUNT, LIMIT_UNLIMITED } from 'shared/constants'
import { PPAddOn, PPProduct } from 'shared/types'
import { StoreDispatch, StoreThunkAction, StoreThunkDispatch, createAppAsyncThunk, getState } from 'store/state'
import { manageAddOns, upgradeSuccess } from './actions'
import { CONTENT_BLOCKING_FEATURE_KEYS } from './constants'
import { V1 } from './net'
import {
  contentUpgradePupupDismissedSelector,
  productsSelector,
  userProductUpgradeSelector,
  userUnlimitedLimitsSelector
} from './selectors'
import { ProductState, initialState } from './state'
import { formatAddOns, mapPurchasedAddOns } from './utils'

export * from './constants'
export * from './selectors'
export * from './utils'

const LIMIT_CONTACT_SUPPORT_MESSAGE = `You've reached the limit for your plan. Please contact us for a custom solution.`

export function getProductById(id: string) {
  return (_dispatch: StoreDispatch, getState: getState) => {
    const products = productsSelector(getState())
    return products.find(p => p.id === id)
  }
}

export function getProductByHandle(handle: string): StoreThunkAction<PPProduct | undefined> {
  return (_dispatch: any, getState: getState) => {
    const products = productsSelector(getState())
    return products.find(p => Object.values(p.handles).includes(handle))
  }
}

export function upgradeProductV2(handle: string): StoreThunkAction<Observable<any>> {
  return (dispatch, getState) => {
    const nextProduct = dispatch(getProductByHandle(handle))
    const isImpersonating = impersonationSelector(getState())
    if (isImpersonating) {
      throw new Error('Cannot upgrade product while impersonating')
    }
    return dispatch(V1.upgradeV2(handle)).pipe(
      map(response => {
        if (response.status === 'error') {
          throw new Error(response.msg)
        }

        dispatch(upgradeSuccess({ newPlan: handle, productInfo: nextProduct }))
        return response.msg
      }),
      tap(() => {
        dispatch(trackHubspotEvent(HS_EVENT_SUBSCRIPTION_CHANGE, { type: 'upgraded' }))
      }),
      catchError(err => {
        return observableThrowError(() => err.response?.error || err)
      })
    )
  }
}

export function getProductUpgradeByFeature(feature: string) {
  return (_dispatch: StoreDispatch, getState: getState) => {
    const products = productsSelector(getState())
    return products.find(product => product.features[feature])
  }
}

export function getProductUpgradeByLimitKey(limit: string, value: number) {
  return (_dispatch: StoreDispatch, getState: getState) => {
    const products = productsSelector(getState())
    return products.find(product => {
      return product.limits[limit] === LIMIT_UNLIMITED || product.limits[limit] >= value
    })
  }
}

/**
 * Checks if user is allowed to use specific feature and opens the Upgrade dialog if not
 *
 * @export
 * @param {string} feature - the feature to check
 * @param {boolean} [checkOnly] - if true, do not show the upgrade dialog
 * @returns {StoreThunkAction<boolean>}
 */
export function checkFeatureAvailability(feature: string, checkOnly?: boolean, closeCallback?: () => void): StoreThunkAction<boolean> {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const state = getState()
    const product = userProductSelector(state)
    if (!product || product.features[feature]) {
      return true
    }

    const productUpgrade = dispatch(getProductUpgradeByFeature(feature))
    if (productUpgrade && !checkOnly) {
      const contentPopupDismissed = contentUpgradePupupDismissedSelector(state)
      const preventPopupOpen = contentPopupDismissed && CONTENT_BLOCKING_FEATURE_KEYS.includes(feature)
      if (preventPopupOpen) {
        return false
      }
      dispatch(promptUpgrade({ feature, product: productUpgrade.handles.annual, closeCallback }))
    }

    return false
  }
}

/**
 * Checks if a value is within current user's Product limits
 * If not - requests to display Upgrade dialog
 *
 * A limit the plan advertises as unlimited has no upgrade to offer, so a rejection there is
 * reported with a generic "contact us" message instead. Callers that show a better message of
 * their own should pass `{ silent: true }` and handle the rejection themselves.
 * Pass `{ checkOnly: true }` to test the limit without opening the upgrade dialog.
 *
 * @returns Boolean
 */
export function checkProductLimit(
  limitKey: string,
  value: number,
  customLimit?: number,
  options?: { silent?: boolean, checkOnly?: boolean }
) {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const state = getState()
    const product = userProductSelector(state)
    if (product) {
      const compareWithLimit = customLimit !== undefined && customLimit > product.limits[limitKey]
        ? customLimit
        : product.limits[limitKey]
      if (compareWithLimit === undefined || compareWithLimit === LIMIT_UNLIMITED || compareWithLimit >= value) {
        return true
      }
    }

    // A limit the plan advertises as unlimited has no meaningful upgrade to offer, even though
    // the enforced cap above can still reject the value. Most callers only `return` on a false
    // result, so say something rather than failing silently.
    if (userUnlimitedLimitsSelector(state)[limitKey]) {
      if (!options?.silent) {
        dispatch(message(LIMIT_CONTACT_SUPPORT_MESSAGE, 'warning'))
      }
      return false
    }

    const productUpgrade = dispatch(getProductUpgradeByLimitKey(limitKey, value))
    if (productUpgrade && !options?.checkOnly) {
      dispatch(promptUpgrade({ feature: limitKey, product: productUpgrade.handles.annual }))
    }

    return false
  }
}

/**
 * Checks the planned-posts limit and, when exceeded, shows the given message
 * and (for errors) prompts the next product upgrade.
 *
 * @returns Boolean - whether the count is within the limit
 */
export function enforcePlannedPostsLimit(
  nextPlannedPostsCount: number,
  limitMessage: string,
  severity: 'error' | 'warning' = 'error'
) {
  return (dispatch: StoreThunkDispatch, getState: getState): boolean => {
    // `limitMessage` names the count and the profile, so it beats the generic fallback in
    // every case - take the rejection back and report it here
    const withinLimit = dispatch(checkProductLimit(LIMIT_SCHEDULED_POSTS_COUNT, nextPlannedPostsCount, undefined, { silent: true }))
    if (!withinLimit) {
      dispatch(message(limitMessage, severity))
      // A plan advertised as unlimited has no upgrade to offer, even though its enforced cap rejected
      const isUnlimited = Boolean(userUnlimitedLimitsSelector(getState())[LIMIT_SCHEDULED_POSTS_COUNT])
      if (severity === 'error' && !isUnlimited) {
        const productUpgrade = userProductUpgradeSelector(getState())
        if (productUpgrade) {
          dispatch(promptUpgrade({ feature: LIMIT_SCHEDULED_POSTS_COUNT, product: productUpgrade.handles.annual }))
        }
      }
    }
    return withinLimit
  }
}

export const getFeaturesAndLimits = createAppAsyncThunk('product/getFeaturesAndLimits', async (_, { dispatch, rejectWithValue }) => {
  try {
    const features = await firstValueFrom(dispatch(getFeatures()))
    const limits = await firstValueFrom(dispatch(getLimits()))
    return { features, limits }
  } catch (e) {
    return rejectWithValue(e)
  }
})

export function endTrial(product: string) {
  return (dispatch: StoreThunkDispatch) => dispatch(V1.endTrial(product))
    .pipe(catchError(err => {
      return observableThrowError(() => err.response.error)
    }))
}

const productSlice = createSlice({
  name: 'product',
  initialState: initialState(),
  reducers: {
    setProducts: (state, action: PayloadAction<PPProduct[]>) => {
      state.products = action.payload
    },
    setAddOns: (
      state, 
      action: PayloadAction<{ list: PPAddOn[], purchased: { active: Record<string, number>, pendingDrops: Record<string, number> } }>) => {
      // Limit keys arrive camelized by the response pipeline - normalise them here so every
      // dispatch site gets it, then rekey the purchased and pending-drop quantities (also camelized) by the real id.
      state.addons = formatAddOns(action.payload.list)
      state.purchasedAddOns = mapPurchasedAddOns(state.addons, action.payload.purchased.active || {})
      state.pendingAddonDowngrades = mapPurchasedAddOns(state.addons, action.payload.purchased.pendingDrops)
    },
    promptUpgrade: (state: ProductState, action: PayloadAction<{ feature: string, product: string, closeCallback?: () => void }>) => {
      state.featureUpgrade = action.payload.feature
      state.productUpgrade = action.payload.product
      state.closeUpgradePopupCallback = action.payload.closeCallback
    },
    closeUpgradePrompt: (state: ProductState) => {
      state.featureUpgrade = undefined
      state.productUpgrade = undefined
      state.closeUpgradePopupCallback = undefined
    },
    toggleContentUpgradePopupDismissed: (state: ProductState, action: PayloadAction<boolean>) => {
      state.displayContentUpgradePopup = !action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getFeaturesAndLimits.fulfilled, (state, action) => {
        state.features = action.payload.features
        state.limits = action.payload.limits
      })
      .addCase(manageAddOns.fulfilled, (state, action) => {
        // No add-ons block means nothing to sync - keep what's known rather than wiping it
        if (!action.payload.addons) {
          return
        }
        state.purchasedAddOns = mapPurchasedAddOns(state.addons, action.payload.addons)
        state.pendingAddonDowngrades = mapPurchasedAddOns(state.addons, action.payload.pendingDrops)
      })
  }
})

export const {
  setProducts,
  setAddOns,
  promptUpgrade,
  closeUpgradePrompt,
  toggleContentUpgradePopupDismissed
} = productSlice.actions
export default productSlice.reducer
